import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FiShield, FiLock, FiSend, FiCheckCircle, FiAlertCircle, FiUser, FiPhone, FiMail, FiKey, FiAlertTriangle, FiMonitor } from '../utils/icons';
import { useUserData } from '../utils/UserDataContext';

const AccountSecurity: React.FC = () => {
  const { userData } = useUserData();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  
  // Mock security settings
  const securitySettings = [
    { id: 'twoFactor', name: 'Two-factor authentication', enabled: true, recommended: true },
    { id: 'loginAlerts', name: 'Login alerts', enabled: true, recommended: true },
    { id: 'deviceManagement', name: 'Device management', enabled: true, recommended: true }, 
    { id: 'locationTracking', name: 'Location-based security', enabled: false, recommended: true },
    { id: 'biometricLogin', name: 'Biometric login', enabled: false, recommended: true },
    { id: 'autoLogout', name: 'Automatic logout', enabled: true, recommended: true },
  ];

  // Mock login activity
  const loginActivity = [
    { id: 1, device: 'Desktop - Chrome', location: 'New York, USA', date: 'Today, 10:23 AM', status: 'current' },
    { id: 2, device: 'iPhone 13', location: 'New York, USA', date: 'Yesterday, 7:45 PM', status: 'normal' },
    { id: 3, device: 'Macbook Pro', location: 'Boston, USA', date: '3 days ago', status: 'suspicious' },
  ];

  // Change password form handler
  const handlePasswordFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would validate and send to an API
    setTimeout(() => {
      setIsChangingPassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Show success notification
    }, 1000);
  };

  const handleToggleSetting = (id: string) => {
    // In a real app, this would update the setting
    console.log(`Toggling setting: ${id}`);
  };

  const getDeviceStatusIcon = (status: string) => {
    switch (status) {
      case 'current':
        return <FiCheckCircle className="text-green-500" size={16} />;
      case 'suspicious':
        return <FiAlertTriangle className="text-red-500" size={16} />;
      default:
        return <FiMonitor className="text-text/50" size={16} />;
    }
  };

  const getDeviceStatusText = (status: string) => {
    switch (status) {
      case 'current':
        return 'Current device';
      case 'suspicious':
        return 'Suspicious activity';
      default:
        return 'Verified device';
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background animate-fadeIn">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">Account Security</h1>
            <p className="text-text/70">Manage your account security settings and monitor activity</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Security Score Card */}
            <div className="lg:col-span-3">
              <Card variant="glass" className="p-6 mb-6">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div className="flex items-center mb-6 md:mb-0">
                    <div className="mr-6 relative">
                      <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                        <circle 
                          className="text-border" 
                          strokeWidth="8" 
                          stroke="currentColor" 
                          fill="transparent" 
                          r="40" 
                          cx="50" 
                          cy="50" 
                        />
                        <circle 
                          className="text-primary" 
                          strokeWidth="8" 
                          stroke="url(#securityGradient)" 
                          fill="transparent" 
                          r="40" 
                          cx="50" 
                          cy="50" 
                          strokeDasharray={`${2 * Math.PI * 40}`}
                          strokeDashoffset={`${2 * Math.PI * 40 * (1 - 75 / 100)}`}
                          strokeLinecap="round"
                        />
                        <defs>
                          <linearGradient id="securityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3a86ff" />
                            <stop offset="100%" stopColor="#4361ee" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-3xl font-bold">75</span>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold">Security Score</h2>
                      <p className="text-text/70">Your account security is good, but could be improved</p>
                      <div className="mt-2 flex items-center">
                        <FiShield className="text-primary mr-2" />
                        <span className="text-sm font-medium">4 out of 6 security features enabled</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Button onClick={() => setIsChangingPassword(true)}>
                      Change Password
                    </Button>
                    <Button variant="outline" onClick={() => setShowTwoFactorSetup(prevState => !prevState)}>
                      {securitySettings[0].enabled ? 'Manage 2FA' : 'Setup 2FA'}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Security Settings */}
            <div className="lg:col-span-2 space-y-6">
              {isChangingPassword && (
                <Card variant="glass" className="p-6 mb-6 animate-fadeIn">
                  <h2 className="text-xl font-semibold mb-4">Change Password</h2>
                  <form onSubmit={handleChangePassword}>
                    <div className="space-y-4">
                      <Input
                        type="password"
                        name="currentPassword"
                        label="Current Password"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordFormChange}
                        leftIcon={<FiLock />}
                        required
                      />
                      <Input
                        type="password"
                        name="newPassword"
                        label="New Password"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordFormChange}
                        leftIcon={<FiLock />}
                        required
                      />
                      <Input
                        type="password"
                        name="confirmPassword"
                        label="Confirm New Password"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordFormChange}
                        leftIcon={<FiLock />}
                        required
                      />
                      <div className="pt-2 flex justify-end space-x-3">
                        <Button 
                          variant="outline" 
                          type="button"
                          onClick={() => setIsChangingPassword(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">
                          Update Password
                        </Button>
                      </div>
                    </div>
                  </form>
                </Card>
              )}

              {showTwoFactorSetup && (
                <Card variant="glass" className="p-6 mb-6 animate-fadeIn">
                  <h2 className="text-xl font-semibold mb-4">Two-Factor Authentication</h2>
                  <div className="mb-4">
                    <p className="text-text/70 mb-4">
                      Two-factor authentication adds an extra layer of security to your account. 
                      Each time you sign in, you'll need to provide a verification code from your phone.
                    </p>
                    
                    <div className="p-4 bg-card rounded-lg mb-4">
                      <div className="flex items-center mb-3">
                        <FiPhone className="text-primary mr-2" size={18} />
                        <span className="font-medium">Verification Methods</span>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="sms"
                            name="verificationMethod"
                            value="sms"
                            checked
                            className="mr-2"
                          />
                          <label htmlFor="sms">SMS Message</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="app"
                            name="verificationMethod"
                            value="app"
                            className="mr-2"
                          />
                          <label htmlFor="app">Authenticator App</label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 mb-4">
                      <Input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Enter verification code"
                        leftIcon={<FiKey />}
                      />
                      <Button>
                        Verify
                      </Button>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      fullWidth 
                      onClick={() => setShowTwoFactorSetup(false)}
                    >
                      Close
                    </Button>
                  </div>
                </Card>
              )}

              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-6">Security Settings</h2>
                <div className="space-y-6">
                  {securitySettings.map((setting) => (
                    <div key={setting.id} className="flex items-center justify-between border-b border-border pb-4 last:border-0 last:pb-0">
                      <div>
                        <div className="flex items-center">
                          <h3 className="font-medium">{setting.name}</h3>
                          {setting.recommended && !setting.enabled && (
                            <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Recommended</span>
                          )}
                        </div>
                        <p className="text-text/70 text-sm mt-1">
                          {setting.enabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={setting.enabled} 
                          onChange={() => handleToggleSetting(setting.id)}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-border rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-6">Connected Accounts</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                    <div className="flex items-center">
                      <div className="bg-[#1DA1F2]/10 p-2 rounded-full mr-3">
                        <FiSend className="text-[#1DA1F2]" size={20} />
                      </div>
                      <div>
                        <h3 className="font-medium">Twitter</h3>
                        <p className="text-text/70 text-sm">Connected</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">Disconnect</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                    <div className="flex items-center">
                      <div className="bg-[#4267B2]/10 p-2 rounded-full mr-3">
                        <FiSend className="text-[#4267B2]" size={20} />
                      </div>
                      <div>
                        <h3 className="font-medium">Facebook</h3>
                        <p className="text-text/70 text-sm">Not connected</p>
                      </div>
                    </div>
                    <Button size="sm">Connect</Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                    <div className="flex items-center">
                      <div className="bg-[#0e76a8]/10 p-2 rounded-full mr-3">
                        <FiSend className="text-[#0e76a8]" size={20} />
                      </div>
                      <div>
                        <h3 className="font-medium">LinkedIn</h3>
                        <p className="text-text/70 text-sm">Not connected</p>
                      </div>
                    </div>
                    <Button size="sm">Connect</Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center mb-1">
                      <FiUser className="text-primary mr-2" size={16} />
                      <span className="text-sm text-text/70">Name</span>
                    </div>
                    <p className="font-medium">{userData.profile.name}</p>
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <FiMail className="text-primary mr-2" size={16} />
                      <span className="text-sm text-text/70">Email</span>
                    </div>
                    <p className="font-medium">{userData.profile.email}</p>
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <FiPhone className="text-primary mr-2" size={16} />
                      <span className="text-sm text-text/70">Phone</span>
                    </div>
                    <p className="font-medium">+1 (555) 123-4567</p>
                  </div>
                </div>
                <Button variant="outline" fullWidth className="mt-4">
                  Edit Information
                </Button>
              </Card>

              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-4">Login Activity</h2>
                <div className="space-y-4">
                  {loginActivity.map(activity => (
                    <div key={activity.id} className="border-b border-border pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start">
                        {getDeviceStatusIcon(activity.status)}
                        <div className="ml-2">
                          <div className="flex items-center">
                            <p className="font-medium">{activity.device}</p>
                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-background">
                              {getDeviceStatusText(activity.status)}
                            </span>
                          </div>
                          <p className="text-sm text-text/70 mt-0.5">{activity.location}</p>
                          <p className="text-xs text-text/50 mt-0.5">{activity.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" fullWidth className="mt-4">
                  View All Activity
                </Button>
              </Card>

              <Card variant="glass" className="p-6">
                <h2 className="text-xl font-semibold mb-4">Security Tips</h2>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <FiAlertCircle className="text-primary mt-0.5 mr-2" size={16} />
                    <p className="text-sm">Use a strong, unique password for your account</p>
                  </div>
                  <div className="flex items-start">
                    <FiAlertCircle className="text-primary mt-0.5 mr-2" size={16} />
                    <p className="text-sm">Enable two-factor authentication for additional security</p>
                  </div>
                  <div className="flex items-start">
                    <FiAlertCircle className="text-primary mt-0.5 mr-2" size={16} />
                    <p className="text-sm">Regularly monitor your account for suspicious activity</p>
                  </div>
                  <div className="flex items-start">
                    <FiAlertCircle className="text-primary mt-0.5 mr-2" size={16} />
                    <p className="text-sm">Be cautious when accessing your account on public networks</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountSecurity; 